<template>
  <v-card flat>
    <v-card-text>
      <v-row class="mb-4">
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="localSearchCode"
            label="Søk på kode"
            append-icon="mdi-magnify"
            outlined
            dense
            @keydown.enter.prevent="handleSearch"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="text-md-right"
        >
          <v-btn
            color="primary"
            text
            @click="handleSearch"
          >
            Søk
          </v-btn>
        </v-col>
      </v-row>

      <v-slide-y-transition>
        <v-alert
          v-if="searchResult"
          dense
          type="info"
          text
          class="mb-4"
        >
          Fant gavekort {{ searchResult.code }} med saldo
          {{ formatCurrency(searchResult.balance, searchResult.currency) }}.
        </v-alert>
      </v-slide-y-transition>

      <v-data-table
        :headers="headers"
        :items="activeCards"
        :loading="loading"
        class="elevation-1"
      >
        <template #[`item.giftCardType`]="{ item }">
          {{ item.giftCardType?.name || "Gavekort" }}
        </template>
        <template #[`item.balance`]="{ item }">
          {{ formatCurrency(item.balance, item.currency) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            small
            :color="statusColor(item.status)"
            text-color="white"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #[`item.soldAt`]="{ item }">
          <span v-if="item.soldAt">{{ formatDate(item.soldAt) }}</span>
          <span v-else>-</span>
        </template>
        <template #[`item.expiryAt`]="{ item }">
          <span v-if="item.expiryAt">{{ formatDate(item.expiryAt) }}</span>
          <span v-else>Ingen</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            small
            @click="$emit('redeem', item)"
          >
            <v-icon color="primary">
              mdi-cash-refund
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import { IGiftCardDTO, GiftCardStatus } from "@shared/types";
import { userModule } from "@/store/modules/user/userModule";

@Component({
  name: "gift-card-active-tab",
})
export default class GiftCardActiveTab extends Vue {
  @Prop({ required: true }) activeCards!: IGiftCardDTO[];
  @Prop({ default: null }) searchResult!: IGiftCardDTO | null;
  @Prop({ default: false }) loading!: boolean;

  public localSearchCode = "";

  get headers() {
    return [
      { text: "Kode", value: "code" },
      { text: "Type", value: "giftCardType" },
      { text: "Saldo", value: "balance" },
      { text: "Status", value: "status" },
      { text: "Solgt", value: "soldAt" },
      { text: "Utløper", value: "expiryAt" },
      { text: "Handlinger", value: "actions", sortable: false },
    ];
  }

  public handleSearch(): void {
    this.$emit("search", this.localSearchCode);
  }

  public formatCurrency(value: number, currency: string | undefined): string {
    const formatter = new Intl.NumberFormat(this.$i18n.locale, {
      style: "currency",
      currency: currency || userModule.user?.currency || "NOK",
    });
    return formatter.format(value);
  }

  public formatDate(date: string | Date | null | undefined): string {
    if (!date) return "-";
    const actual = typeof date === "string" ? new Date(date) : date;
    return actual.toLocaleDateString(this.$i18n.locale);
  }

  public statusLabel(status: GiftCardStatus): string {
    const map: Record<GiftCardStatus, string> = {
      [GiftCardStatus.Generated]: "Generert",
      [GiftCardStatus.Printed]: "Skrevet ut",
      [GiftCardStatus.Active]: "Aktiv",
      [GiftCardStatus.Redeemed]: "Innløst",
      [GiftCardStatus.PartiallyRedeemed]: "Delvis innløst",
      [GiftCardStatus.Expired]: "Utgått",
      [GiftCardStatus.Voided]: "Annullert",
    };
    return map[status];
  }

  public statusColor(status: GiftCardStatus): string {
    switch (status) {
      case GiftCardStatus.Active:
        return "green";
      case GiftCardStatus.Generated:
      case GiftCardStatus.Printed:
        return "blue";
      case GiftCardStatus.Redeemed:
        return "success";
      case GiftCardStatus.Expired:
      case GiftCardStatus.Voided:
        return "grey";
      default:
        return "primary";
    }
  }
}
</script>
